import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import { StatefulButton } from '@edx/paragon';
import PropTypes from 'prop-types';

import ArrowButton from '../../common-components/ArrowButton';
import StepsProgressBar from '../../common-components/StepsProgressBar';
import { COMPLETE_STATE } from '../../data/constants';
import { ALL_ACADEMIC_EDUCATION } from '../data/constants';
import { allValuesFalse, atLeastOneValueIsTrue, removeFalseValueKeyFromObject } from '../../data/utils/dataUtils';
import { saveUserProfile } from '../../progressive-profiling/data/actions';
import StepRenderer from '../components/StepRenderer';
import useStepsData from '../hooks/useStepsData';
import messages from '../messages';

const AdditionalUserInformation = ({
  handleGoToNextStep,
  handleGoToPrevStep,
}) => {
  const { formatMessage } = useIntl();

  const {
    registrationFormData: backedUpFormData,
  } = useSelector(state => state.register);

  const { authenticatedUser, optionalFields } = useSelector(state => state.commonComponents);

  const { submitState } = useSelector(state => state.welcomePage);

  const [activeStep, setActiveStep] = useState(0);
  const [additionalUserInformation, setAdditionalUserInformation] = useState({
    country: 'IL',
    ...backedUpFormData.additionalUserInformation,
  });

  const {
    submitState: additionalUserInformationSubmitState,
  } = useSelector(state => state.welcomePage);

  const dispatch = useDispatch();

  const steps = useStepsData(optionalFields, formatMessage);

  const stepData = steps[activeStep];

  const currentStepValueSelected = stepData.fieldKey instanceof Array
    ? atLeastOneValueIsTrue(stepData.fieldKey, additionalUserInformation)
    : additionalUserInformation[stepData.fieldKey];

  const handleNextStep = () => setActiveStep(prev => prev + 1);
  const handlePrevStep = () => setActiveStep(prev => prev - 1);

  const handleGoToSpecificStep = (step) => {
    setActiveStep(step);
  };

  const handleSelect = (fieldKey, value) => {
    setAdditionalUserInformation(prev => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleContinue = (isSkippingLastStep = false) => {
    let updatedAdditionalInfo = { ...additionalUserInformation };

    if (updatedAdditionalInfo.level_of_education === ALL_ACADEMIC_EDUCATION && updatedAdditionalInfo.academic_degree) {
      updatedAdditionalInfo.level_of_education = updatedAdditionalInfo.academic_degree;
    }

    if (currentStepValueSelected) {
      if (activeStep !== steps.length - 1) {
        handleNextStep();
      }
    } else if (!isSkippingLastStep) {
      return;
    }

    if (activeStep === steps.length - 1) {
      if (allValuesFalse(additionalUserInformation)) {
        handleGoToNextStep();
        return;
      }

      const payload = {
        username: authenticatedUser.username,
        data: removeFalseValueKeyFromObject(updatedAdditionalInfo),
      };

      dispatch(saveUserProfile(payload));
    }
  };

  const handleSkip = () => {
    if (activeStep === steps.length - 1) {
      handleContinue(true);
    } else {
      handleNextStep();
    }
  };

  useEffect(() => {
    if (additionalUserInformationSubmitState === COMPLETE_STATE) {
      handleGoToNextStep();
    }
  }, [additionalUserInformationSubmitState, handleGoToNextStep]);

  const [stepChanged, setStepChanged] = useState(true);

  useEffect(() => {
    setStepChanged(true);
  }, [activeStep]);

  useEffect(() => {
    if (currentStepValueSelected) setStepChanged(false);
  }, [currentStepValueSelected]);

  return (
    <div className="additional-user-information d-flex justify-content-center align-items-start flex-fill">
      <div className="content-container h-100 d-flex justify-content-between align-items-center flex-column">
        <div className="w-100 flex-center-column question-container">
          <StepsProgressBar
            activeStep={activeStep}
            steps={steps}
            onClick={handleGoToSpecificStep}
          />
          <StepRenderer
            stepData={stepData}
            additionalInformation={additionalUserInformation}
            onSelect={handleSelect}
          />
        </div>
        <div className="w-100 actions-container">
          <div className='start-actions'>
            <ArrowButton
              onClick={activeStep === 0 ? handleGoToPrevStep : handlePrevStep}
            />
            <StatefulButton
              id="next-step"
              name="next-step"
              type="button"
              className="logistration-btn"
              style={{
                opacity: currentStepValueSelected && !stepChanged ? 1 : 0.6,
                pointerEvents: currentStepValueSelected && !stepChanged  ? 'auto' : 'none',
              }}
              state={submitState}
              labels={{
                default: formatMessage(messages['continue.button']),
                pending: '',             
              }}
              onClick={() => handleContinue()}
            />
          </div>
          <div className="end-actions">
            <ArrowButton
              text={formatMessage(messages['skip.question.button'])}
              iconPosition="left"
              onClick={handleSkip}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

AdditionalUserInformation.propTypes = {
  handleGoToNextStep: PropTypes.func.isRequired,
  handleGoToPrevStep: PropTypes.func.isRequired,
};

export default AdditionalUserInformation;
